"use client";

import * as React from "react";

import { usePrefersReducedMotion } from "./use-prefers-reduced-motion";

interface CapturedRect {
  /** The presentation the capture was taken FOR (the incoming one). */
  forPresentation: string;
  rect: DOMRect | null;
}

/**
 * FLIP continuity for a popup that swaps between overlay presentations —
 * e.g. a fullscreen Dialog and a right-side Sheet that render the same
 * content. The two presentations are different overlay roots, so switching
 * unmounts one popup and mounts the other; with no shared element the switch
 * pops. This hook manufactures the continuity: attach the returned ref to
 * whichever popup is mounted, and when `presentation` changes it captures the
 * outgoing popup's rect (First), measures the incoming popup after mount
 * (Last), and plays the inverted delta back to identity.
 *
 * The keyframes drive the individual `scale`/`translate`/`opacity` properties
 * — the same ones the popups' enter transitions use — so for its duration the
 * animation overrides the stock mount transition instead of fighting it.
 *
 * The outgoing rect is read during render: once the presentation prop has
 * changed, the pre-commit DOM (still holding the OLD popup) is the only place
 * that rect exists, so no wrapped setter is needed at the call site.
 */
export function usePresentationFlip(
  presentation: string,
): React.RefObject<HTMLDivElement | null> {
  const popupRef = React.useRef<HTMLDivElement | null>(null);
  const prevRef = React.useRef(presentation);
  const capturedRef = React.useRef<CapturedRect | null>(null);
  const reduceMotion = usePrefersReducedMotion();

  if (prevRef.current !== presentation) {
    prevRef.current = presentation;
    capturedRef.current = {
      forPresentation: presentation,
      rect: popupRef.current?.getBoundingClientRect() ?? null,
    };
  }

  React.useLayoutEffect(() => {
    const captured = capturedRef.current;
    const first =
      captured?.forPresentation === presentation ? captured.rect : null;
    if (!first || reduceMotion) return;
    // One frame out: sibling layout effects (e.g. anchor-inset margins) whose
    // state flushes land after this effect; rAF still runs before that same
    // paint, so the popup is measured settled and never flashes unanimated.
    const frame = requestAnimationFrame(() => {
      // Consumed only when the frame actually fires — a Strict Mode re-run
      // cancels the first frame in cleanup and replays with the capture
      // intact, so the flip survives the double-invoke.
      capturedRef.current = null;
      const el = popupRef.current;
      if (!el) return;
      const last = el.getBoundingClientRect();
      if (!last.width || !last.height) return;
      el.animate(
        [
          {
            opacity: 1,
            scale: `${first.width / last.width} ${first.height / last.height}`,
            transformOrigin: "0 0",
            translate: `${first.left - last.left}px ${first.top - last.top}px`,
          },
          {
            opacity: 1,
            scale: "1 1",
            transformOrigin: "0 0",
            translate: "0px 0px",
          },
        ],
        { duration: 240, easing: "cubic-bezier(0.22, 1, 0.36, 1)" },
      );
    });
    return () => cancelAnimationFrame(frame);
  }, [presentation, reduceMotion]);

  return popupRef;
}
